"use client";

import { updatePriceMap } from "@/app/directories/shop/lib/MarketCore";
import { getHrtsByOrganizationId } from "@frontend/lib/actions/actions";
import { useBoundStore } from "@frontend/lib/stores/globalStore";
import type { PricingMapEntries } from "@frontend/lib/stores/slices/pricingMapSlice";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { z } from "zod";
import { bundleToHrts } from "./products/[productId]/bundleConstants";

const StoreFrontConsumerSchema = z.object({
  fname: z.string().min(1, "First name is required"),
  lname: z.string().min(1, "Last name is required"),
  dob: z
    .string()
    .regex(/^\d{2}-\d{2}-\d{4}$/, "Date must be in MM-DD-YYYY format"),
});

// Insurance schema
const StorefrontInsuranceSchema = z.object({
  memberId: z.string().min(1, "Member ID is required"),
  payer: z.string().min(1, "Payer is required"),
  state: z.string().length(2, "State must be a 2-letter code"),
});

// Full schema with array of insurances
const FullSchema = z.object({
  consumer: StoreFrontConsumerSchema,
  insurances: z
    .array(StorefrontInsuranceSchema)
    .min(1, "At least one insurance is required"),
});

export function PriceLoader({ orgId }: { orgId: number }) {
  const priceMap = useBoundStore.use.pricingMapEntries();
  const consumer = useBoundStore.use.consumer();
  const insurances = useBoundStore.use.insurances();
  const setPricingMap = useBoundStore.use.setPricingMap();
  const resetPricingMap = useBoundStore.use.resetPricingMap();
  const setPricingStatus = useBoundStore.use.setPricingStatus();
  const resetStore = useBoundStore.use.resetAllStores();
  const setStorefrontPaymentMode = useBoundStore.use.setStorefrontPaymentMode();
  const isPendingPricesMode = useBoundStore.use.isPendingPricesMode();
  const isInsuranceValidated = useBoundStore.use.isInsuranceValidated();

  const user = useBoundStore.use.user();

  // Fetch HRTs using React Query
  const { data: hrts = [] } = useQuery({
    queryKey: ["getHrts", orgId],
    queryFn: () => getHrtsByOrganizationId(orgId),
  });

  const availableHrts = hrts.filter((hrt) => {
    const isDirectlyAvailable =
      hrt.isConsumerFacing && hrt.productToHrt.length > 0;

    // Check if this HRT is part of any bundle that contains an available HRT
    const isPartOfAvailableBundle = Array.from(bundleToHrts.values()).some(
      (bundleHrts) => {
        // If this HRT is in the bundle
        if (bundleHrts.includes(hrt.hrtId)) {
          // Check if any HRT in this bundle is consumer-facing with products
          return hrts.some(
            (otherHrt) =>
              bundleToHrts.has(otherHrt.hrtId) && otherHrt.isConsumerFacing,
          );
        }
        return false;
      },
    );

    return isDirectlyAvailable || isPartOfAvailableBundle;
  });

  const { hrtIds, insurableHrtIds } = partitionHrts(availableHrts);

  // Serialize consumer and insurances for queryKey to avoid React Query v5 issues
  const consumerKey = consumer
    ? `${consumer.id || ""}-${consumer.fname || ""}-${consumer.dob || ""}`
    : null;
  const insurancesKey = insurances
    ? JSON.stringify(
        insurances.map((i) => ({
          payer: i.payer,
          memberId: i.memberId,
          state: i.state,
        })),
      )
    : null;

  const { data: updatedPriceMap, error } = useQuery({
    queryKey: [
      "updatePrices",
      hrtIds,
      consumerKey,
      insurancesKey,
      orgId,
      insurableHrtIds,
      // Include pending-prices mode so flipping PROCEED_WITH_UNVERIFIED
      // (`isPendingPricesMode: false → true`) re-fetches the price map
      // with `forceGenerateInsuredPricedTreatments: true`. Without this
      // the cached CASH-only map from the pre-pending sync would persist
      // and `useCartItem` would charge the CASH price (e.g. $225) instead
      // of the $0 pending-prices placeholder the backend otherwise emits.
      isPendingPricesMode,
      // Include eligibility so that when insurance is validated
      // (`isInsuranceValidated: false → true`) this query refetches and
      // `updatePriceMap` backfills the INSURED prices that were skipped while
      // coverage was still unverified. See INSURED gating note below.
      isInsuranceValidated,
    ],
    queryFn: () =>
      updatePriceMap(
        hrtIds,
        consumer,
        insurances,
        orgId,
        new Set(insurableHrtIds),
        priceMap,
        "",
        isInsuranceValidated,
        isPendingPricesMode,
      ),
    // Don't fire until the HRTs this org offers have loaded — before then
    // `hrtIds` is `[]` and the request has nothing to price. This also
    // prevents a redundant early call while `getHrts` is still in-flight.
    //
    // NOTE: this query stays enabled regardless of `isInsuranceValidated` so
    // CASH and anonymous pricing always fetch immediately. The INSURED portion
    // is now gated *inside* `getPrices` on `isInsuranceValidated` (the combined
    // price map still fetches cash unconditionally, only the insured branch
    // waits for eligibility). Because `isInsuranceValidated` is part of this
    // query's key, flipping it true refetches and `updatePriceMap`'s
    // incremental merge backfills the previously-skipped INSURED slots without
    // disturbing the existing CASH entries. See PR for rationale.
    enabled: hrtIds.length > 0,
    // Scoped guardrails for the coverage/eligibility/pricing round-trip:
    // treat a just-fetched price map as fresh for a few minutes so the
    // insurance-connect flow (which re-keys this query as store fields
    // change) coalesces onto one in-flight request instead of re-hitting the
    // payer, and don't auto-refetch on focus/reconnect. See PR for rationale.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const hasInsuranceContext = FullSchema.safeParse({
    consumer: consumer,
    insurances: insurances,
  }).success;

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (updatedPriceMap) {
      resetPricingMap();
      setPricingMap(updatedPriceMap);

      // Check if user has explicitly selected CASH mode in the updated pricing map
      const hasExplicitCashMode = Object.values(updatedPriceMap).some(
        (entry) => entry?.mode === "CASH",
      );

      // PRIORITY: If pricing map has CASH modes, always set to CASH (user's explicit selection)
      // This overrides any previous INSURED setting
      if (hasExplicitCashMode) {
        setStorefrontPaymentMode("CASH");
      } else if (
        FullSchema.safeParse({ consumer: consumer, insurances: insurances })
          .success
      ) {
        setPricingStatus("SUCCESS");
        // Only flip the storefront into INSURED pricing when the insurance
        // was actually verified active (`isInsuranceValidated`) or the user
        // explicitly opted into the pending-prices flow. Without this gate
        // a saved-but-non-active insurance (inactive / out_of_network /
        // unverified-no-flag) would still flip the global mode to
        // "INSURED" here — and downstream the price toggle would render
        // $0 / $NaN because there's no INSURED price data for those rows.
        if (isInsuranceValidated || isPendingPricesMode) {
          setStorefrontPaymentMode("INSURED");
        } else {
          setStorefrontPaymentMode("CASH");
        }
      } else {
        setPricingStatus("NEUTRAL");
      }
    } else {
      /**
       * Is insurance pricing updated?
       */
      if (
        FullSchema.safeParse({ consumer: consumer, insurances: insurances })
          .success
      ) {
        setPricingStatus("SUCCESS");
      } else {
        setPricingStatus("NEUTRAL");
      }
    }
  }, [updatedPriceMap, isPendingPricesMode, consumer, insurances]);

  useEffect(() => {
    const currentOrgId = user?.orgId;
    if (currentOrgId && currentOrgId !== orgId) {
      resetStore();
    }
  }, [orgId, user?.orgId, resetStore]);

  return null;
}

// Helper functions
function partitionHrts(hrts) {
  return hrts.reduce(
    (acc, hrt) => {
      acc.hrtIds.push(hrt.hrtId);
      if (hrt.hrtPaymentType.some((type) => type.paymentType === "INSURED")) {
        acc.insurableHrtIds.push(hrt.hrtId);
      }
      return acc;
    },
    { hrtIds: [] as number[], insurableHrtIds: [] as number[] },
  );
}

function arePriceModesAvailableForAll(
  priceMap: PricingMapEntries,
  hrtIds: number[],
  mode: "CASH" | "INSURED",
) {
  return hrtIds.every((hrtId) => {
    const prices = priceMap[hrtId];
    return prices?.priceData[mode];
  });
}
